import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Layers, ShieldAlert, Server } from 'lucide-react';
import { useRunGroup, useRunGroupAttempts } from '../hooks/useRunGroups';
import { desktopDeepLinks } from '../lib/deep-links';
import { DeepLinkButton, StatusPill, Field } from '../components/operator-ui';
import EvidencePanel from '../components/EvidencePanel';
import ReconciliationPanel from '../components/ReconciliationPanel';

/**
 * WI-14 (OPERATOR-UI-PLACEMENT): run-group DETAIL view. Renders the full
 * read-only projection (status, generation, projected_generation/projected_at,
 * worker binding) plus its attempts. Selecting an attempt reveals the read-only
 * evidence ledger and reconciliation state. Every actionable control is a typed
 * hand-off to the native Aldan Desktop console — the console never mutates.
 */
export default function RunGroupDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: runGroup, isLoading } = useRunGroup(id);
  const { data: attempts, isLoading: attemptsLoading } = useRunGroupAttempts(id);
  const [selectedAttemptId, setSelectedAttemptId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-sm text-slate-400">Loading run group…</div>
      </div>
    );
  }

  if (!runGroup) {
    return (
      <div className="p-8">
        <Link
          to="/operator"
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Operator Console
        </Link>
        <div className="text-sm text-slate-500">Run group not found.</div>
      </div>
    );
  }

  const selectedAttempt = attempts?.find((a) => a.id === selectedAttemptId) ?? null;

  return (
    <div className="p-8">
      <div className="mb-6">
        <Link
          to="/operator"
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Operator Console
        </Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
              <Layers className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-mono text-xl font-bold text-slate-900">{runGroup.id}</h1>
                <StatusPill status={runGroup.status} />
              </div>
              <p className="mt-0.5 text-sm text-slate-500">
                generation {runGroup.generation}
                {runGroup.projectedGeneration != null && (
                  <> · projected {runGroup.projectedGeneration}</>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <DeepLinkButton href={desktopDeepLinks.runGroup(runGroup.id)} label="Open run group" />
            {runGroup.workerId && (
              <DeepLinkButton href={desktopDeepLinks.worker(runGroup.workerId)} label="Open worker" />
            )}
          </div>
        </div>
      </div>

      {/* Projection */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Projection</h2>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="Status" value={runGroup.status} />
          <Field label="Generation" value={runGroup.generation} />
          <Field label="Projected generation" value={runGroup.projectedGeneration} />
          <Field
            label="Projected at"
            value={runGroup.projectedAt ? new Date(runGroup.projectedAt).toLocaleString() : null}
          />
          <Field
            label="Worker binding"
            value={
              runGroup.workerId ? (
                <Link
                  to={`/operator/workers/${runGroup.workerId}`}
                  className="inline-flex items-center gap-1 text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                >
                  <Server className="h-3.5 w-3.5 text-slate-400" />
                  {runGroup.workerId}
                </Link>
              ) : null
            }
            mono
          />
          <Field label="Active instance" value={runGroup.activeInstanceId} mono />
          <Field label="Checkpoint ref" value={runGroup.checkpointRef} mono />
          <Field label="Project" value={runGroup.projectId} mono />
          <Field
            label="Created"
            value={runGroup.createdAt ? new Date(runGroup.createdAt).toLocaleString() : null}
          />
          <Field
            label="Updated"
            value={runGroup.updatedAt ? new Date(runGroup.updatedAt).toLocaleString() : null}
          />
        </dl>
      </div>

      {/* Attempts */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Attempts</h2>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Attempt</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Gen</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {attemptsLoading && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                    Loading attempts…
                  </td>
                </tr>
              )}
              {!attemptsLoading && (attempts?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                    No attempts yet
                  </td>
                </tr>
              )}
              {attempts?.map((a) => {
                const needsReconcile = a.status === 'outcome-unknown';
                const isSelected = a.id === selectedAttemptId;
                return (
                  <tr
                    key={a.id}
                    onClick={() => setSelectedAttemptId(a.id)}
                    className={`cursor-pointer text-slate-700 ${
                      isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{a.id}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          needsReconcile
                            ? 'inline-flex items-center gap-1 text-amber-600'
                            : ''
                        }
                      >
                        {needsReconcile && <ShieldAlert className="h-3.5 w-3.5" />}
                        {a.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{a.attempt ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{a.generation ?? '—'}</td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-2">
                        <DeepLinkButton href={desktopDeepLinks.attempt(a.id)} label="Attempt" />
                        {needsReconcile && (
                          <DeepLinkButton
                            href={desktopDeepLinks.reconciliation(a.id)}
                            label="Reconcile"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(attempts?.length ?? 0) > 0 && !selectedAttempt && (
          <p className="mt-3 text-xs text-slate-400">
            Select an attempt to inspect its evidence ledger and reconciliation state.
          </p>
        )}
      </div>

      {/* Selected attempt: evidence + reconciliation */}
      {selectedAttempt && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>Inspecting attempt</span>
            <span className="font-mono text-slate-900">{selectedAttempt.id}</span>
          </div>
          <ReconciliationPanel instanceId={selectedAttempt.id} />
          <EvidencePanel instanceId={selectedAttempt.id} />
        </div>
      )}
    </div>
  );
}
